import { useSelector } from 'react-redux';
import { call, put, select } from 'redux-saga/effects';

import {
  HYDRATE,
  type HydrateAction,
} from '@/domains/core/applicationState/store/hydrateAction';
import { fetchReviewsStats } from '@/domains/productDiscovery/ArticleReviews/articleReviews.api';
import type {
  ReviewsStats,
  ReviewsStatsSate,
} from '@/domains/productDiscovery/ArticleReviews/ArticleReviews.model';
import {
  GET_DETAIL_SUCCESS,
  GRAPHQL_SUCCESS,
  GraphqlSuccess,
  type GetDetailSuccess,
} from '@/domains/productDiscovery/ProductPage/detail.actions';

import type { SpartacuxProductDiscoveryDomainState } from '../productDiscovery.model';

// ACTIONS
export const GET_REVIEWS_STATS = 'product/GET_REVIEWS_STATS';

export interface GetReviewsStats {
  type: typeof GET_REVIEWS_STATS;
}

export const GET_REVIEWS_STATS_SUCCESS = 'product/GET_REVIEWS_STATS_SUCCESS';

export interface GetReviewsStatsSuccess {
  type: typeof GET_REVIEWS_STATS_SUCCESS;
  reviewsStats: ReviewsStats;
}

export const GET_REVIEWS_STATS_FAILURE = 'product/GET_REVIEWS_STATS_FAILURE';

export interface GetReviewsStatsFailure {
  type: typeof GET_REVIEWS_STATS_FAILURE;
  error: string;
}

// REDUCER
export type Action =
  | GetReviewsStats
  | GetReviewsStatsSuccess
  | GetReviewsStatsFailure
  | GetDetailSuccess;

export const initialReviewsStatsState: ReviewsStatsSate = {
  fetching: false,
  fetched: false,
  articleId: null,
  average: 0,
  total: 0,
  distribution: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 },
};

// SELECTORS
export const reviewsStatsSelector = (
  state: SpartacuxProductDiscoveryDomainState,
) =>
  state?.productDiscovery?.articleReviews?.reviewsStats ||
  initialReviewsStatsState;

export const useReviewsStats = () => useSelector(reviewsStatsSelector);

export default function reducer(
  state: ReviewsStatsSate = initialReviewsStatsState,
  action: Action | GraphqlSuccess | HydrateAction,
): ReviewsStatsSate {
  switch (action.type) {
    case HYDRATE:
      return reviewsStatsSelector(action.state);

    case GET_REVIEWS_STATS: {
      return {
        ...state,
        fetching: true,
        fetched: false,
        articleId: null,
      };
    }
    case GET_REVIEWS_STATS_SUCCESS: {
      return {
        ...action.reviewsStats,
        fetching: false,
        fetched: true,
      };
    }
    case GET_REVIEWS_STATS_FAILURE:
      return {
        ...initialReviewsStatsState,
        error: action.error,
        fetching: false,
        fetched: true,
      };

    case GET_DETAIL_SUCCESS:
      if (action.detail.reviewsStats) {
        return {
          ...action.detail.reviewsStats,
          articleId: action.detail.articleId,
          fetching: false,
          fetched: true,
        };
      }
      return initialReviewsStatsState;

    case GRAPHQL_SUCCESS: {
      const { payload } = action;

      if (!payload?.ratingsStats)
        return { ...initialReviewsStatsState, fetched: true };

      return {
        average: payload.ratingsStats.average,
        total: payload.ratingsStats.total,
        distribution: {
          '1': payload.ratingsStats.distribution.one,
          '2': payload.ratingsStats.distribution.two,
          '3': payload.ratingsStats.distribution.three,
          '4': payload.ratingsStats.distribution.four,
          '5': payload.ratingsStats.distribution.five,
        },
        articleId: Number(payload.ratingsStats.articleId),
        fetching: false,
        fetched: true,
      };
    }

    default:
      return state;
  }
}

// SAGA
interface ActionWithArticleId {
  type: string;
  articleId: number;
}

export function* getReviewsStatsSaga(
  action: ActionWithArticleId,
): Generator<any, any, any> {
  try {
    const reviewsStatsState = yield select(reviewsStatsSelector);
    if (
      reviewsStatsState.fetched &&
      reviewsStatsState.articleId === action.articleId
    ) {
      return null;
    }

    yield put({
      type: GET_REVIEWS_STATS,
    });

    const reviewsStats = yield call(fetchReviewsStats, action.articleId);

    yield put({
      type: GET_REVIEWS_STATS_SUCCESS,
      reviewsStats,
    });
  } catch (error) {
    yield put({
      type: GET_REVIEWS_STATS_FAILURE,
      error: error.message,
    });
  }
}
